import React, { useState } from 'react';
import apiClient from '../../services/EnhancedApiClient';

const PaymentTrackingDebugPanel = () => {
  const [loading, setLoading] = useState(false);
  const [systemStatus, setSystemStatus] = useState(null);
  const [debugLogs, setDebugLogs] = useState([]);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [testData, setTestData] = useState({
    email: 'test@example.com',
    amount: 99.99,
    orderNumber: `TEST-${Date.now()}`,
    failureReason: 'Card declined by issuer'
  });
  const [diagnosticResults, setDiagnosticResults] = useState(null);
  const [showTestForm, setShowTestForm] = useState(false);
  const [showCardNumbers, setShowCardNumbers] = useState(false);

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const newLog = {
      id: Date.now() + Math.random(),
      timestamp,
      message,
      type
    };
    setDebugLogs(prev => [...prev, newLog]);
    console.log(`[${timestamp}] ${message}`);
  };

  const clearLogs = () => {
    setDebugLogs([]);
  };

  const exportLogs = () => {
    const logText = debugLogs.map(log => 
      `[${log.timestamp}] ${log.type.toUpperCase()}: ${log.message}`
    ).join('\n');
    
    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payment-debug-logs-${new Date().toISOString().slice(0,10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const runFullDiagnostics = async () => {
    setLoading(true);
    addLog("🚀 Starting full diagnostics...", "info");

    const results = {
      kvStorage: false,
      existingDeclines: 0,
      testDecline: false,
      storageWorkflow: false,
      kvBindings: false,
    };

    try {
      // Test 1: KV Storage
      addLog("1️⃣ Testing KV Storage...", "info");
      const kvResponse = await apiClient.get("/api/admin/test-kv-storage");

      if (kvResponse.paymentTracking?.success) {
        addLog("✅ KV Storage is working", "success");
        results.kvStorage = true;
      } else {
        addLog(
          `❌ KV Storage issues: ${
            kvResponse.paymentTracking?.error || "Unknown"
          }`,
          "error"
        );
        setSystemStatus({
          type: "error",
          message: "KV Storage is not working",
        });
        setDiagnosticResults(results);
        setLoading(false);
        return;
      }

      // Test 2: Check existing declines
      addLog("2️⃣ Checking existing declines...", "info");
      const historyResponse = await apiClient.fetchCardDeclineHistory(true);
      const declineCount = historyResponse.declines?.length || 0;
      results.existingDeclines = declineCount;
      addLog(`📊 Found ${declineCount} existing declines`, "info");

      // Test 3: KV Bindings
      addLog("3️⃣ Testing KV bindings...", "info");
      try {
        const bindingsResponse = await apiClient.get(
          "/api/admin/check-kv-bindings"
        );
        const paymentTrackingExists =
          bindingsResponse.bindings?.PAYMENT_TRACKING?.exists;

        if (paymentTrackingExists) {
          addLog("✅ KV bindings are correct", "success");
          results.kvBindings = true;
        } else {
          addLog("❌ PAYMENT_TRACKING binding missing", "error");
        }
      } catch (error) {
        addLog(`⚠️ Could not check KV bindings: ${error.message}`, "warning");
      }

      // Test 4: Storage workflow
      addLog("4️⃣ Testing storage workflow...", "info");
      try {
        const workflowResponse = await apiClient.get(
          "/api/admin/test-decline-storage"
        );
        const allTestsPassed = workflowResponse.tests?.every(
          (test) => test.success
        );

        if (allTestsPassed) {
          addLog("✅ Storage workflow tests passed", "success");
          results.storageWorkflow = true;
        } else {
          addLog("❌ Some storage workflow tests failed", "error");
          workflowResponse.tests?.forEach((test) => {
            if (!test.success) {
              addLog(`   ❌ ${test.test}: ${test.error || "Failed"}`, "error");
            }
          });
        }
      } catch (error) {
        addLog(
          `⚠️ Could not test storage workflow: ${error.message}`,
          "warning"
        );
      }

      // Test 5: Create test decline
      addLog("5️⃣ Creating test decline...", "info");
      const testResult = await createTestDecline(true);
      results.testDecline = testResult;

      // NEW: Test 6 - Card Decline Storage Test
      addLog("6️⃣ Testing card decline storage workflow...", "info");
      try {
        const cardDeclineResponse = await apiClient.get(
          "/api/admin/debug-payment-tracking"
        );

        if (cardDeclineResponse.cardDeclineTest?.success) {
          addLog("✅ Card decline storage workflow working", "success");
          results.cardDeclineWorkflow = true;

          // Check specific metrics
          if (cardDeclineResponse.cardDeclineTest.stored) {
            addLog("✅ Card declines are being stored in KV", "success");
          } else {
            addLog("❌ Card declines not being stored in KV", "error");
          }

          if (cardDeclineResponse.cardDeclineTest.retrievable) {
            addLog("✅ Stored card declines are retrievable", "success");
          } else {
            addLog("❌ Stored card declines not retrievable", "error");
          }

          // Show performance metrics
          if (cardDeclineResponse.cardDeclineTest.timeMs) {
            const timeMs = cardDeclineResponse.cardDeclineTest.timeMs;
            const status =
              timeMs < 2000 ? "success" : timeMs < 5000 ? "warning" : "error";
            addLog(`⏱️ Card decline storage time: ${timeMs}ms`, status);
          }
        } else {
          addLog(
            `❌ Card decline storage failed: ${
              cardDeclineResponse.cardDeclineTest?.error || "Unknown error"
            }`,
            "error"
          );
          results.cardDeclineWorkflow = false;
        }

        // Display card decline counts
        const cardDeclineCount =
          cardDeclineResponse.totalCounts?.cardDeclines || 0;
        addLog(
          `📊 Found ${cardDeclineCount} card declines in storage`,
          cardDeclineCount > 0 ? "success" : "warning"
        );
      } catch (cardDeclineError) {
        addLog(
          `❌ Card decline test failed: ${cardDeclineError.message}`,
          "error"
        );
        results.cardDeclineWorkflow = false;
      }

      // NEW: Test 7 - KV Performance Analysis
      addLog("7️⃣ Analyzing KV performance...", "info");
      try {
        const perfResponse = await apiClient.get(
          "/api/admin/debug-payment-tracking"
        );

        if (perfResponse.kvPerformance) {
          const perf = perfResponse.kvPerformance;
          const successRate = (perf.successfulTests / perf.totalTests) * 100;

          addLog(
            `📈 KV Performance: ${successRate.toFixed(1)}% success rate`,
            successRate >= 80 ? "success" : "warning"
          );

          if (perf.averageResponseTime) {
            const avgTime = perf.averageResponseTime.toFixed(0);
            addLog(
              `⏱️ Average KV response time: ${avgTime}ms`,
              avgTime < 1000 ? "success" : avgTime < 3000 ? "warning" : "error"
            );
          }

          if (perf.timeouts > 0) {
            addLog(`⏰ KV timeouts detected: ${perf.timeouts}`, "error");
          }

          results.kvPerformance = successRate >= 80;
        }
      } catch (perfError) {
        addLog(`❌ KV performance test failed: ${perfError.message}`, "error");
        results.kvPerformance = false;
      }

      // Update the diagnostic results to include new tests
      setDiagnosticResults({
        ...results,
        cardDeclineWorkflow: results.cardDeclineWorkflow || false,
        kvPerformance: results.kvPerformance || false,
      });

      // Final status
      const allPassed =
        results.kvStorage && results.testDecline && results.storageWorkflow;

      if (allPassed) {
        setSystemStatus({
          type: "success",
          message: "✅ All diagnostics passed - System is working!",
        });
        addLog("🎉 All diagnostics passed!", "success");
      } else {
        setSystemStatus({
          type: "error",
          message: "❌ Some diagnostics failed - Check logs for details",
        });
        addLog("⚠️ Some diagnostics failed - see details above", "warning");
      }
    } catch (error) {
      addLog(`❌ Diagnostics failed: ${error.message}`, "error");
      setSystemStatus({ type: "error", message: "Diagnostics failed" });
    } finally {
      setDiagnosticResults(results);
      setLoading(false);
    }
  };

  const createTestDecline = async (silent = false) => {
    if (!silent) addLog("🧪 Creating test decline...", "info");

    try {
      const response = await apiClient.post(
        "/api/admin/test-card-decline",
        testData
      );

      if (response.success) {
        if (!silent) {
          addLog(
            `✅ Test decline created: ${response.result?.declineId}`,
            "success"
          );
          // Refresh decline history after a delay
          setTimeout(() => {
            addLog("🔍 Checking if test decline appears in history...", "info");
            checkDeclineHistory();
          }, 2000);
        }
        return true;
      } else {
        addLog(
          `❌ Test decline creation failed: ${
            response.error || "Unknown error"
          }`,
          "error"
        );
        return false;
      }
    } catch (error) {
      addLog(`❌ Test decline error: ${error.message}`, "error");
      return false;
    }
  };

  const checkDeclineHistory = async () => {
    addLog("📋 Checking decline history...", "info");

    try {
      const response = await apiClient.fetchCardDeclineHistory(true);

      addLog(`📊 Decline History:`, "info");
      addLog(
        `   Total Count: ${response.totalCount || response.count || 0}`,
        "info"
      );
      addLog(`   Returned Count: ${response.declines?.length || 0}`, "info");

      if (response.declines?.length > 0) {
        addLog("Recent declines:", "info");
        response.declines.slice(0, 5).forEach((decline, index) => {
          addLog(
            `   ${index + 1}. ${decline.orderNumber} - ${decline.email} - $${
              decline.amount
            }`,
            "info"
          );
        });
      } else {
        addLog("⚠️ No declines found in history", "warning");
      }
    } catch (error) {
      addLog(`❌ Failed to check decline history: ${error.message}`, "error");
    }
  };

  const checkKVStorage = async () => {
    addLog("🗄️ Checking KV Storage details...", "info");

    try {
      const response = await apiClient.get("/api/admin/debug-payment-tracking");

      addLog("📊 KV Storage Status:", "info");
      addLog(
        `   Total Declines: ${response.totalCounts?.declines || 0}`,
        "info"
      );
      addLog(
        `   Total Failed Auth: ${response.totalCounts?.failedAuth || 0}`,
        "info"
      );

      const sampleKeys =
        response.declineKeys
          ?.slice(0, 3)
          .map((k) => k.name)
          .join(", ") || "None";
      addLog(`   Sample Keys: ${sampleKeys}`, "info");

      if (response.testDecline?.stored && response.testDecline?.retrieved) {
        addLog("✅ KV read/write test passed", "success");
      } else {
        addLog("❌ KV read/write test failed", "error");
      }
    } catch (error) {
      addLog(`❌ KV Storage check failed: ${error.message}`, "error");
    }
  };

  const startPaymentMonitoring = () => {
    if (!isMonitoring) {
      addLog("👀 Starting payment request monitoring...", "info");

      // Store original fetch
      const originalFetch = window.fetch;

      // Override fetch to monitor payment requests
      window.fetch = async function (...args) {
        const [url] = args;

        if (
          typeof url === "string" &&
          (url.includes("payment") ||
            url.includes("place_order") ||
            url.includes("checkout"))
        ) {
          addLog(
            `🔍 Payment request: ${url.substring(url.lastIndexOf("/") + 1)}`,
            "info"
          );

          try {
            const response = await originalFetch.apply(this, args);

            if (response.status >= 400) {
              addLog(
                `🚨 HTTP Error: ${response.status} ${response.statusText}`,
                "error"
              );
            } else {
              addLog(`✅ Response OK: ${response.status}`, "success");
            }

            return response;
          } catch (error) {
            addLog(`❌ Request failed: ${error.message}`, "error");
            throw error;
          }
        }

        return originalFetch.apply(this, args);
      };

      setIsMonitoring(true);
      addLog("✅ Payment monitoring active", "success");
    } else {
      addLog("⏹️ Stopping payment monitoring...", "info");
      setIsMonitoring(false);
      addLog("✅ Payment monitoring stopped", "info");
    }
  };

  const getLogStyle = (type) => {
    const baseStyle = {
      padding: "8px 12px",
      margin: "2px 0",
      borderRadius: "4px",
      fontFamily: "monospace",
      fontSize: "14px",
      borderLeft: "4px solid",
    };

    switch (type) {
      case "success":
        return {
          ...baseStyle,
          backgroundColor: "#d4edda",
          borderLeftColor: "#28a745",
          color: "#155724",
        };
      case "error":
        return {
          ...baseStyle,
          backgroundColor: "#f8d7da",
          borderLeftColor: "#dc3545",
          color: "#721c24",
        };
      case "warning":
        return {
          ...baseStyle,
          backgroundColor: "#fff3cd",
          borderLeftColor: "#ffc107",
          color: "#856404",
        };
      default:
        return {
          ...baseStyle,
          backgroundColor: "#d1ecf1",
          borderLeftColor: "#17a2b8",
          color: "#0c5460",
        };
    }
  };

  const getStatusStyle = (type) => {
    const baseStyle = {
      padding: "12px 16px",
      borderRadius: "4px",
      marginBottom: "16px",
      border: "1px solid",
    };

    switch (type) {
      case "success":
        return {
          ...baseStyle,
          backgroundColor: "#d4edda",
          borderColor: "#c3e6cb",
          color: "#155724",
        };
      case "error":
        return {
          ...baseStyle,
          backgroundColor: "#f8d7da",
          borderColor: "#f5c6cb",
          color: "#721c24",
        };
      default:
        return {
          ...baseStyle,
          backgroundColor: "#d1ecf1",
          borderColor: "#bee5eb",
          color: "#0c5460",
        };
    }
  };

  const buttonStyle = {
    padding: "8px 16px",
    margin: "4px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#007bff",
    color: "white",
  };

  const successButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#28a745",
    color: "white",
  };

  const dangerButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#dc3545",
    color: "white",
  };

  const outlineButtonStyle = {
    ...buttonStyle,
    backgroundColor: "white",
    color: "#007bff",
    border: "1px solid #007bff",
  };

  return (
    <div
      style={{
        backgroundColor: "white",
        padding: "24px",
        borderRadius: "8px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        marginBottom: "24px",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <h2 style={{ margin: 0, color: "#333" }}>
          🔍 Payment Tracking Debug Tool
        </h2>
        <div
          style={{
            padding: "4px 12px",
            backgroundColor: "#e9ecef",
            borderRadius: "16px",
            fontSize: "12px",
            color: "#6c757d",
          }}
        >
          Debug Mode
        </div>
      </div>

      {/* System Status */}
      <div style={{ marginBottom: "24px" }}>
        <h3 style={{ color: "#666", marginBottom: "16px" }}>
          📊 System Status
        </h3>

        {systemStatus ? (
          <div style={getStatusStyle(systemStatus.type)}>
            {systemStatus.message}
          </div>
        ) : (
          <div style={getStatusStyle("info")}>
            Click "Run Full Diagnostics" to check system status
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: "8px",
            flexWrap: "wrap",
            marginBottom: "16px",
          }}
        >
          <button
            style={
              loading
                ? { ...successButtonStyle, opacity: 0.6 }
                : successButtonStyle
            }
            onClick={runFullDiagnostics}
            disabled={loading}
          >
            {loading ? "Running..." : "🚀 Run Full Diagnostics"}
          </button>
          <button style={outlineButtonStyle} onClick={checkKVStorage}>
            🗄️ Check KV Storage
          </button>
          <button style={outlineButtonStyle} onClick={checkDeclineHistory}>
            📋 Check Decline History
          </button>
        </div>

        {loading && (
          <div
            style={{
              width: "100%",
              height: "4px",
              backgroundColor: "#e9ecef",
              borderRadius: "2px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: "30%",
                height: "100%",
                backgroundColor: "#007bff",
                animation: "loading 2s ease-in-out infinite",
              }}
            />
          </div>
        )}
      </div>

      {/* Main Content Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: window.innerWidth > 768 ? "1fr 1fr" : "1fr",
          gap: "24px",
          marginBottom: "24px",
        }}
      >
        {/* Manual Tests */}
        <div
          style={{
            border: "1px solid #dee2e6",
            borderRadius: "8px",
            padding: "16px",
          }}
        >
          <h4 style={{ marginTop: 0, color: "#333" }}>🧪 Manual Tests</h4>

          <div style={{ marginBottom: "16px" }}>
            <button
              style={outlineButtonStyle}
              onClick={() => setShowTestForm(!showTestForm)}
            >
              {showTestForm ? "▼" : "▶"} Create Test Decline
            </button>

            {showTestForm && (
              <div
                style={{
                  marginTop: "12px",
                  padding: "16px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "4px",
                }}
              >
                <div style={{ marginBottom: "12px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "4px",
                      fontSize: "14px",
                    }}
                  >
                    Email:
                  </label>
                  <input
                    type="email"
                    value={testData.email}
                    onChange={(e) =>
                      setTestData({ ...testData, email: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  />
                </div>

                <div style={{ marginBottom: "12px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "4px",
                      fontSize: "14px",
                    }}
                  >
                    Amount:
                  </label>
                  <input
                    type="number"
                    value={testData.amount}
                    onChange={(e) =>
                      setTestData({
                        ...testData,
                        amount: parseFloat(e.target.value),
                      })
                    }
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  />
                </div>

                <div style={{ marginBottom: "12px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "4px",
                      fontSize: "14px",
                    }}
                  >
                    Order Number:
                  </label>
                  <input
                    type="text"
                    value={testData.orderNumber}
                    onChange={(e) =>
                      setTestData({ ...testData, orderNumber: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  />
                </div>

                <div style={{ marginBottom: "12px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "4px",
                      fontSize: "14px",
                    }}
                  >
                    Failure Reason:
                  </label>
                  <select
                    value={testData.failureReason}
                    onChange={(e) =>
                      setTestData({
                        ...testData,
                        failureReason: e.target.value,
                      })
                    }
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  >
                    <option value="Card declined by issuer">
                      Card declined by issuer
                    </option>
                    <option value="Insufficient funds">
                      Insufficient funds
                    </option>
                    <option value="Invalid card information">
                      Invalid card information
                    </option>
                    <option value="Card expired">Card expired</option>
                    <option value="Invalid CVV">Invalid CVV</option>
                  </select>
                </div>

                <button
                  style={successButtonStyle}
                  onClick={() => createTestDecline(false)}
                >
                  Create Test Decline
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Real Payment Testing */}
        <div
          style={{
            border: "1px solid #dee2e6",
            borderRadius: "8px",
            padding: "16px",
          }}
        >
          <h4 style={{ marginTop: 0, color: "#333" }}>
            🎯 Real Payment Testing
          </h4>

          <div style={{ marginBottom: "16px" }}>
            <h5 style={{ marginBottom: "8px" }}>Monitor Real Payments</h5>
            <p
              style={{
                fontSize: "14px",
                color: "#6c757d",
                marginBottom: "12px",
              }}
            >
              Monitor payment requests in real-time
            </p>

            <button
              style={isMonitoring ? dangerButtonStyle : primaryButtonStyle}
              onClick={startPaymentMonitoring}
            >
              {isMonitoring ? "⏹️ Stop Monitoring" : "▶️ Start Monitoring"}
            </button>
          </div>

          <div>
            <button
              style={outlineButtonStyle}
              onClick={() => setShowCardNumbers(!showCardNumbers)}
            >
              {showCardNumbers ? "▼" : "▶"} Test Card Numbers
            </button>

            {showCardNumbers && (
              <div
                style={{
                  marginTop: "12px",
                  padding: "16px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "4px",
                }}
              >
                <p style={{ fontSize: "14px", marginBottom: "8px" }}>
                  Use these to trigger real failures:
                </p>
                <ul
                  style={{
                    fontSize: "14px",
                    margin: "0",
                    paddingLeft: "20px",
                    fontFamily: "monospace",
                  }}
                >
                  <li>
                    <code>4000000000000002</code> - Card declined
                  </li>
                  <li>
                    <code>4000000000000127</code> - Incorrect CVC
                  </li>
                  <li>
                    <code>4000000000000069</code> - Expired card
                  </li>
                  <li>
                    <code>4242424242424241</code> - Invalid number
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Diagnostic Results */}
      {diagnosticResults && (
        <div style={{ marginBottom: "24px" }}>
          <h3 style={{ color: "#666", marginBottom: "16px" }}>
            📋 Diagnostic Results
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: "12px",
            }}
          >
            <div
              style={{
                padding: "8px 12px",
                border: `2px solid ${
                  diagnosticResults.kvStorage ? "#28a745" : "#dc3545"
                }`,
                borderRadius: "16px",
                textAlign: "center",
                fontSize: "14px",
                color: diagnosticResults.kvStorage ? "#28a745" : "#dc3545",
              }}
            >
              {diagnosticResults.kvStorage ? "✅" : "❌"} KV Storage
            </div>

            <div
              style={{
                padding: "8px 12px",
                border: `2px solid ${
                  diagnosticResults.testDecline ? "#28a745" : "#dc3545"
                }`,
                borderRadius: "16px",
                textAlign: "center",
                fontSize: "14px",
                color: diagnosticResults.testDecline ? "#28a745" : "#dc3545",
              }}
            >
              {diagnosticResults.testDecline ? "✅" : "❌"} Test Decline
            </div>

            <div
              style={{
                padding: "8px 12px",
                border: `2px solid ${
                  diagnosticResults.storageWorkflow ? "#28a745" : "#dc3545"
                }`,
                borderRadius: "16px",
                textAlign: "center",
                fontSize: "14px",
                color: diagnosticResults.storageWorkflow
                  ? "#28a745"
                  : "#dc3545",
              }}
            >
              {diagnosticResults.storageWorkflow ? "✅" : "❌"} Workflow
            </div>

            <div
              style={{
                padding: "8px 12px",
                border: `2px solid ${
                  diagnosticResults.existingDeclines > 0 ? "#28a745" : "#6c757d"
                }`,
                borderRadius: "16px",
                textAlign: "center",
                fontSize: "14px",
                color:
                  diagnosticResults.existingDeclines > 0
                    ? "#28a745"
                    : "#6c757d",
              }}
            >
              {diagnosticResults.existingDeclines} Declines
            </div>
          </div>
        </div>
      )}

      {/* Debug Log */}
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <h3 style={{ margin: 0, color: "#666" }}>
            📝 Debug Log ({debugLogs.length})
          </h3>
          <div>
            <button style={outlineButtonStyle} onClick={clearLogs}>
              🗑️ Clear
            </button>
            <button style={outlineButtonStyle} onClick={exportLogs}>
              💾 Export
            </button>
          </div>
        </div>

        <div
          style={{
            border: "1px solid #dee2e6",
            borderRadius: "4px",
            maxHeight: "300px",
            overflow: "auto",
            backgroundColor: "#f8f9fa",
          }}
        >
          {debugLogs.length === 0 ? (
            <div
              style={{
                padding: "20px",
                textAlign: "center",
                color: "#6c757d",
              }}
            >
              No debug logs yet. Run diagnostics to see output.
            </div>
          ) : (
            <div>
              {debugLogs.slice(-20).map((log) => (
                <div key={log.id} style={getLogStyle(log.type)}>
                  <div style={{ fontSize: "12px", opacity: 0.8 }}>
                    {log.timestamp}
                  </div>
                  <div>{log.message}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <style>
        {`
          @keyframes loading {
            0% { transform: translateX(-100%); }
            50% { transform: translateX(0%); }
            100% { transform: translateX(100%); }
          }
        `}
      </style>
    </div>
  );
};

export default PaymentTrackingDebugPanel;